import React, { useEffect, useState } from "react";
import axios from "axios";
import { Eye, Trash2, RotateCcw, Plus, CheckCircle, XCircle, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "./TransferList.css";

const url = process.env.REACT_APP_BACKEND_URL;

const TransferList = () => {
  const [transfers, setTransfers] = useState([]);
  const navigate = useNavigate();

  const fetchTransfers = async () => {
    try {
      const response = await axios.get(`${url}/api/transfer/all`);
      setTransfers(response.data.data);
    } catch (error) {
      console.error("Error fetching transfers:", error);
      toast.error("Failed to fetch transfers");
    }
  };

  useEffect(() => {
    fetchTransfers();
  }, []);

  const deleteTransfer = async (id) => {
    if (window.confirm("Are you sure you want to delete this transfer record?")) {
      try {
        await axios.delete(`${url}/api/transfer/${id}`);
        toast.success("Transfer deleted successfully");
        fetchTransfers();
      } catch (error) {
        toast.error("Failed to delete transfer");
      }
    }
  };

  const restoreTransfer = async (id) => {
    try {
      await axios.put(`${url}/api/transfer/${id}/restore`);
      toast.success("Transfer restored successfully");
      fetchTransfers();
    } catch (error) {
      toast.error("Failed to restore transfer");
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { class: "status-pending", text: "Pending", icon: <Clock size={14} /> },
      approved: { class: "status-approved", text: "Approved", icon: <CheckCircle size={14} /> },
      rejected: { class: "status-rejected", text: "Rejected", icon: <XCircle size={14} /> },
      completed: { class: "status-completed", text: "Completed", icon: <CheckCircle size={14} /> },
    };
    return badges[status] || { class: "status-pending", text: status };
  };

  return (
    <div className="transfer-container">
      <div className="transfer-header">
        <h2>Transfer History</h2>
        <button onClick={() => navigate('/AddTransfer')} className="add-button">
          <Plus size={16} /> New Transfer
        </button>
      </div>

      <div className="transfer-stats">
        <div className="stat-card">
          <div className="stat-value">{transfers.length}</div>
          <div className="stat-label">Total Transfers</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">
            {transfers.filter(t => t.transfer_status === "completed").length}
          </div>
          <div className="stat-label">Completed</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">
            {transfers.filter(t => t.transfer_status === "pending").length}
          </div>
          <div className="stat-label">Pending</div>
        </div>
      </div>

      <div className="table-responsive">
        <table className="transfer-table">
          <thead>
            <tr>
              <th>Transfer No</th>
              <th>Date</th>
              <th>Customer</th>
              <th>From Client</th>
              <th>To Client</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {transfers.map((transfer) => (
              <tr key={transfer._id}>
                <td>#{transfer.transfer_no}</td>
                <td>{new Date(transfer.transfer_date).toLocaleDateString()}</td>
                <td>
                  <div className="customer-info">
                    <strong>{transfer.original_customer_id?.fullname}</strong>
                    <small>{transfer.original_customer_id?.phone}</small>
                  </div>
                </td>
                <td>
                  <div className="client-info">
                    <span>{transfer.original_client_id?.name}</span>
                  </div>
                </td>
                <td>
                  <div className="client-info">
                    <span className="new-client">{transfer.new_client_id?.name}</span>
                  </div>
                </td>
                <td>
                  <span className={`status-badge ${getStatusBadge(transfer.transfer_status).class}`}>
                    {getStatusBadge(transfer.transfer_status).text}
                  </span>
                </td>
                <td className="actions">
                  <button
                    onClick={() => navigate(`/transfer/view/${transfer._id}`)}
                    className="view-btn"
                    title="View Details"
                  >
                    <Eye size={16} />
                  </button>

                  {transfer.status === "active" ? (
                    <button
                      className="delete-btn"
                      onClick={() => deleteTransfer(transfer._id)}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  ) : (
                    <button
                      className="restore-btn"
                      onClick={() => restoreTransfer(transfer._id)}
                      title="Restore"
                    >
                      <RotateCcw size={16} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {transfers.length === 0 && (
        <div className="empty-state">
          <p>No transfers found</p>
          <button onClick={() => navigate('/AddTransfer')} className="add-button">
            Create First Transfer
          </button>
        </div>
      )}
    </div>
  );
};

export default TransferList;